import { ActionResponse, DomainDetailHook, DomainType, UIFeedbackHooks } from "@/Shared/Types";
import { ModalHook, useBreakPoint } from "./TechnicalHooks";
import { ActionGroups, DOMAIN_CONFIGS, UIAction, useUIActions } from "@/Shared";
import { useAppNavigation } from "./useAppNavigation";
import { useEffect, useRef } from "react";
import { useAppDispatch, usePermission } from "@/OneLiners";
import { uiStateActions } from "../Redux";
import { useAccessRight } from "@/Domains";
import { ActionStatusHelper } from "@/Shared/Helpers";


export function useDomainDetail(
    domainType: DomainType,
    uiFeedbackHooks: UIFeedbackHooks = {} as UIFeedbackHooks,
    inModalHook?: ModalHook,
    extraArgs?: {
        entityId?: number
    }
): {
    entityVM: any,
    response: ActionResponse,
    UIActions : ActionGroups
} {

    const dispatch = useAppDispatch();

    /** Resolve domain-specific detail configuration */
    const { useDetailHook } = DOMAIN_CONFIGS[domainType];

    const { goToErrorPage } = useAppNavigation();

    const permissionHook = usePermission();

    const accessRight = useAccessRight(permissionHook.dataMap);


    /** Instantiate the domain-specific detail hook with UI feedback */
    const {
        requestId,
        computeUIActions,
        entityVM,
        executeDelete,
        executeRestore,
        deleteStatus,
        restoreStatus,
        response: detailHookResponse
    } = useDetailHook?.({
        accessRight,
        uiFeedbackHooks,
        permissionHook,
        inModalHook,
        extraArgs
    }
    ) as DomainDetailHook<any>;

    const response = {
        status: ActionStatusHelper.combine(
            detailHookResponse.status,
            permissionHook.stateResponse.status,
            // accessRight.status
        ),
        error: detailHookResponse.error ??
            permissionHook.stateResponse.error
    }

    /**
     * Guard to prevent multiple redirects during async state transitions
     */
    const hasRedirected = useRef(false);

    const { computeUIActions: getCommonUIActions } = useUIActions({
        inModal : !!inModalHook,
        modalHook : inModalHook ?? uiFeedbackHooks.modalHook,
        domainType,
        accessRight,
        id: requestId,
        isDeleted : entityVM?.isDeleted,
        executeDelete,
        executeRestore,
        deleteLoading: deleteStatus ? ActionStatusHelper.isLoading(deleteStatus) : false,
        restoreLoading: restoreStatus ? ActionStatusHelper.isLoading(restoreStatus) : false,
    });


    useEffect(() => {
        if (!accessRight?.hasEnums || hasRedirected.current) return;

        if (ActionStatusHelper.isSucceeded(response.status) && !accessRight.canRead(domainType)) {
            hasRedirected.current = true;
            goToErrorPage(403);
        }
    }, [accessRight?.hasEnums, domainType, response.status]);

    /** Sync current domain type with global UI state */
    useEffect(() => {
        dispatch(uiStateActions.setCurDomainType(domainType));
        if (requestId) {
            dispatch(
                uiStateActions.removeBadge({
                    key: domainType,
                    values: [requestId]
                })
            );
        }
    }, [domainType, dispatch, requestId]);

    const UIActions = [
        ...getCommonUIActions(entityVM?.model),
        ...(computeUIActions?.() ?? [])
    ].reduce<ActionGroups>(
        (acc, action) => {
            acc[action.placement].push(action);
            return acc;
        },
        {
            header: [],
            sub: [],
            media : []
        }
    );


    return {
        entityVM,
        response,
        UIActions
    };
}